import {
  Box,
  Button,
  Flex,
  Select,
  InputGroup,
  InputLeftElement,
  Input,
  Heading,
  Text,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Image,
  Grid,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  GridItem,
} from "@chakra-ui/react";
import { TbReload as ReloadIcon } from "react-icons/tb";
import { MdApproval  as ApprovalIcon} from "react-icons/md";
import { MdMoreVert as MoreIcon } from "react-icons/md";
import { LuSearch as SearchIcon } from "react-icons/lu";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAtom, atom } from "jotai";
import { modalAtom, openAtom, notificationAtom, openNAtom } from "../../../context";
import apiCall from "../../../utils/apiCall";
import { format } from "date-fns";
import ReactPaginate from "react-paginate";
import styled from "@emotion/styled";
import { Loader } from "../../../customs";
import { TopDisplay } from "../../../components";

// Atoms
const indexAtom = atom<number | null>(null);

// Styles
const HeadStyle = {
  borderBottom: "1px solid var(--dark-green-color)",
  padding: "15px 5px",
};

const DataStyle = {
  padding: "5px 5px",
  thcolor: "var(--dark-green-color)",
};

const ROWSTYLE = styled.tr`
  & > td:last-of-type {
    width: 50px;
    height: 63px;
    & > div {
      display: none;
    }
  }
  &:hover > * {
    cursor: pointer;
    background-color: var(--table-highlight);
  }
  & > td {
    width: 140px;
  }
  &:hover > td:last-of-type {
    & > div {
      display: block;
    }
  }
  & > td > div.active {
    display: block;
  }
`;

const StylePaginateBox = styled.div`
  width: 100%;
  & > ul {
    list-style-type: none;
    text-align: center;
    padding: 0;
    margin: 0;
    margin-top: 10px;
  }
  & > ul > li {
    display: inline;
    margin: 0 5px;
    padding: 2px 5px;
    border-radius: 3px;
    background-color: transparent;
    color: var(--dark-green-color);
  }
  & > ul > li.selected {
    background-color: var(--highlight-paginate-background);
    color: var(--highlight-paginate-color);
  }
  & > ul > li:first-of-type,
  & > ul > li:last-of-type {
    font-weight: bold;
  }
`;

// Main Component
export default function PictureApproval() {
  const [search, setSearch] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [filterData, setFilterData] = useState<any[]>([]);
  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 100;
  const endOffset = itemOffset + itemsPerPage;
  const currentItems = filterData?.slice(itemOffset, endOffset);
  const pageCount = Math.ceil(filterData?.length / itemsPerPage);

  const { data: allRequest, isLoading } = useQuery(
    ["all-request", selectedStatus || "en"],
    async () => {
      const firstPageResponse = await apiCall().get(
        `Exam/getAllStudentBiodataApprovals?selected_status=${selectedStatus || ""}&PageNumber=1&PageSize=100`
      );
      let totalPages = firstPageResponse.data?.PageCount || 1;
      let allData = firstPageResponse.data?.reportItems || [];

      if (totalPages > 1) {
        const fetchPromises = [];
        for (let page = 2; page <= totalPages; page++) {
          fetchPromises.push(
            apiCall().get(
              `Exam/getAllStudentBiodataApprovals?selected_status=${selectedStatus || ""}&PageNumber=${page}&PageSize=100`
            )
          );
        }
        const responses = await Promise.all(fetchPromises);
        responses.forEach((res) => {
          allData = [...allData, ...(res.data?.reportItems || [])];
        });
      }
      return allData;
    },
    { select: (data) => data }
  );

  useEffect(() => {
    setFilterData(allRequest || []);
  }, [allRequest]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    if (val) {
      const newData = filterData?.filter((eData) =>
        Object.values(eData).join("").toLowerCase().includes(val.toLowerCase())
      );
      setFilterData(newData.length > 0 ? newData : allRequest);
    } else {
      setFilterData(allRequest);
    }
  };

  const handlePageClick = (event: { selected: number }) => {
    const newOffset = (event.selected * itemsPerPage) % filterData?.length;
    setItemOffset(newOffset);
  };

  return (
    <Box>
      <TopDisplay label="Picture Request Approval In Options"/>
      <Flex gap="20px" wrap="wrap" mt="20px">
        <InputGroup h="40px" width="250px" bg="var(--white-color)" borderColor="var(--dark-green-color)" pos="relative">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input value={search} onChange={handleSearch} type="text" placeholder="search" />
          <span style={{ position: "absolute", zIndex: "1", right: "10px", cursor: "pointer", top: "12px" }}>
            <ReloadIcon />
          </span>
        </InputGroup>
        <Select
          placeholder="Status"
          width="130px"
          bg="var(--white-color)"
          borderColor="var(--dark-green-color)"
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option>All</option>
          <option>Approved</option>
          <option>Rejected</option>
          <option>Pending</option>
        </Select>
      </Flex>
      <Flex mt="25px" flexDir="column" gap="10px">
        <Heading m="0" fontSize="1rem">
          All Picture Request Approvals
        </Heading>
        <Text m="0" fontSize=".8rem">
          Showing {itemOffset} - {itemOffset + itemsPerPage > filterData?.length ? filterData?.length : endOffset} of {filterData?.length}
        </Text>
      </Flex>
      {isLoading ? (
        <Loader />
      ) : (
        <TableContainer mt="20px">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th sx={HeadStyle}>Student No</Th>
                <Th sx={HeadStyle}>Student Name</Th>
                <Th sx={HeadStyle}>Approval-Type</Th>
                <Th sx={HeadStyle}>Created</Th>
                <Th sx={HeadStyle}>ActionBy</Th>
                <Th sx={HeadStyle}>Old Picture</Th>
                <Th sx={HeadStyle}>New Picture</Th>
                <Th sx={HeadStyle}>Status</Th>
                <Th sx={HeadStyle} w="50px"></Th>
              </Tr>
            </Thead>
            <Tbody>
              {currentItems.map((eCandidate, index) => (
                <ROWSTYLE key={eCandidate.Id}>
                  <Td sx={DataStyle}>{eCandidate.StudentNo}</Td>
                  <Td sx={DataStyle}>{eCandidate.StudentName || "N/A"}</Td>
                  <Td sx={DataStyle}>{eCandidate.ApprovalType}</Td>
                  <Td sx={DataStyle}>
                    {format(new Date(eCandidate.ActionDate), "dd/MM/yyyy")}
                    <br />
                    {format(new Date(eCandidate.CreateDate), "hh:mm a")}
                  </Td>
                  <Td sx={DataStyle}>{eCandidate.ActionBy}</Td>
                  <Td>
                    <Image src={eCandidate.OldImageUrl} alt="Old Picture" boxSize="50px" />
                  </Td>
                  <Td>
                    <Image src={eCandidate.NewImageUrl} alt="New Picture" boxSize="50px" />
                  </Td>
                  <Td sx={DataStyle}>{eCandidate.ApprovalStatus}</Td>
                  <Td sx={DataStyle} minW="60px" w="50px" h="50px">
                    <div>
                      <Option index={index} candidate={eCandidate} />
                    </div>
                  </Td>
                </ROWSTYLE>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      <StylePaginateBox>
        <ReactPaginate
          breakLabel="..."
          nextLabel=">"
          onPageChange={handlePageClick}
          pageRangeDisplayed={5}
          pageCount={pageCount}
          previousLabel="<"
          renderOnZeroPageCount={null}
        />
      </StylePaginateBox>
    </Box>
  );
}

// Option Component
const Option = (props: any) => {
  const [ind, set_index] = useAtom(indexAtom);
  const [, setOpen] = useAtom(openAtom);
  const [, setModal] = useAtom(modalAtom);

  const onClick = () => {
    if (ind === props.index) {
      set_index(null);
      return;
    }
    set_index(props.index);
  };

  const handleShowProfile = () => {
    set_index(null);
    setModal({
      header: 'Student Details',
      content: <ViewProfile candidate={props.candidate} />,
    });
    setOpen(true);
  };

  const handleSingleApproval = () => {
    if (!props.candidate) {
      console.error("Candidate data is missing");
      return;
    }
  
    set_index(null);
    setModal({
      header: "Picture Request Approval",
      content: <ViewSingleApproval {...props.candidate} ApprovalId={props.candidate.Id} />,
    });
    setOpen(true);
  };
  
  const sx = {
    cursor: 'pointer',
    _hover: { backgroundColor: 'gray.200' },
  };
  
  return (
    <Popover>
      <PopoverTrigger>
        <Button onClick={() => set_index(props.index)} bg="var(--cipm-light-green)">
          <MoreIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent width="fit-content">
        <PopoverArrow />
        <PopoverBody>
          <Grid templateColumns="1fr" p="6px">
            <GridItem sx={sx} p="8px 4px" onClick={handleShowProfile}>
              View Details
            </GridItem>
            <GridItem sx={sx} p="8px 4px" onClick={handleSingleApproval}>
              Approve/Reject
            </GridItem>
          </Grid>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};


// ViewProfile Component
const ViewProfile = (props: any) => (
  <Flex flexDir="column" minWidth="600px" gap="50px">
    <Flex align="center" gap="20px">
      <Text width="150px" fontWeight="bold" color="var(--light-text)">
        Student NO:
      </Text>
      <Text>{props.candidate.StudentNo}</Text>
    </Flex>
    <Grid templateColumns="1fr" templateRows="repeat(3,1fr)" gap="30px">
      <GridItem as={Flex} gap="20px">
        <Text width="150px" fontWeight="bold" color="var(--light-text)">
          Student Name:
        </Text>
        <Text fontWeight="500">{props.candidate.StudentName || "N/A"}</Text>
      </GridItem>
      <GridItem as={Flex} gap="20px" alignItems="center">
        <Text width="150px" fontWeight="bold" color="var(--light-text)">
          Created Date:
        </Text>
        <Text fontWeight="500">
          {format(new Date(props?.candidate.CreateDate), "dd/MM/yyyy")}
          <br />
          {format(new Date(props?.candidate.CreateDate), "hh:mm a")}
        </Text>
      </GridItem>
      <GridItem as={Flex} gap="20px" alignItems="center">
        <Text width="150px" fontWeight="bold" color="var(--light-text)">
          Approved Date:
        </Text>
        <Text fontWeight="500">
          {format(new Date(props?.candidate.ActionDate), "dd/MM/yyyy")}
          <br />
          {format(new Date(props?.candidate.ActionDate), "hh:mm a")}
        </Text>
      </GridItem>
      <GridItem as={Flex} gap="20px">
        <Text width="150px" fontWeight="bold" color="var(--light-text)">
          ApproveBy:
        </Text>
        <Text fontWeight="500">{props.candidate.ActionBy || "N/A"}</Text>
      </GridItem>
      <GridItem as={Flex} gap="20px">
        <Text width="150px" fontWeight="bold" color="var(--light-text)">
          Approval Type:
        </Text>
        <Text fontWeight="500">{props.candidate.ApprovalType || "N/A"}</Text>
      </GridItem>
      <GridItem as={Flex} flexDir="column" align="center">
        <Heading>Old Picture</Heading>
        {props?.candidate?.OldImageUrl ? (
          <Image src={props.candidate.OldImageUrl.trim()} alt="Old Picture" maxWidth="80vw" maxHeight="90vh" objectFit="contain" />
        ) : (
          <Text>No Old Picture Available</Text>
        )}
      </GridItem>
      <GridItem as={Flex} flexDir="column" align="center">
        <Heading>New Picture</Heading>
        {props?.candidate?.NewImageUrl ? (
          <Image src={props.candidate.NewImageUrl.trim()} alt="New Picture" maxWidth="80vw" maxHeight="90vh" objectFit="contain" />
        ) : (
          <Text>No New Picture Available</Text>
        )}
      </GridItem>
      <GridItem as={Flex} gap="20px">
        <Text width="150px" fontWeight="bold" color="var(--light-text)">
          Status:
        </Text>
        <Text fontWeight="500">{props.candidate.ApprovalStatus || "Pending"}</Text>
      </GridItem>
    </Grid>
  </Flex>
);

// ViewSingleApproval Component
const ViewSingleApproval = (props: any) => {
  const [, setOpen] = useAtom(openAtom);
  const [, setNOpen] = useAtom(openNAtom);
  const [, setModal] = useAtom(modalAtom);
  const [, setNotification] = useAtom(notificationAtom);
  const queryClient = useQueryClient();

  // Log the props to debug
  //console.log("ViewSingleApproval Props:", props);

  // Separate mutation for "Approve"
  const {
    isLoading: isApproving,
    mutate: handleApprove,
  } = useMutation({
    mutationKey: ["approve-request"],
    mutationFn: async (approvalId: number) => {
      //console.log("Approving with ApprovalId:", approvalId); // Debugging
      try {
        const response = await apiCall().post(
          `/Exam/approveOrRejectProfilePicture?ApprovalId=${approvalId}&ApprovalChoice=APPROVE`,
          {}
        );
        //console.log("Approve API Response:", response); // Log the response
        return response;
      } catch (error) {
        console.error("Approve API Error:", error); // Log the error
        throw error; // Re-throw the error to trigger onError
      }
    },
    onSuccess(data: any) {
      //console.log("Approve Success:", data); // Debugging
      setNotification({
        type: "success",
        message: data.data?.Message || "Picture Approved Successfully",
      });
      setNOpen(true);
      queryClient.invalidateQueries(["all-picture-request"]);
    },
    onError(error: any) {
      console.error("Approve Error:", error); // Debugging
      setNotification({
        type: "error",
        message: error.response?.data?.Message || "Error Occurred",
      });
      setNOpen(true);
    },
    onSettled() {
      //console.log("Approve Settled"); // Debugging
      setOpen(false);
    },
  });

  // Separate mutation for "Reject"
  const {
    isLoading: isRejecting,
    mutate: handleReject,
  } = useMutation({
    mutationKey: ["reject-request"],
    mutationFn: async (approvalId: number) => {
      //console.log("Rejecting with ApprovalId:", approvalId); // Debugging
      try {
        const response = await apiCall().post(
          `/Exam/approveOrRejectProfilePicture?ApprovalId=${approvalId}&ApprovalChoice=REJECT`,
          {}
        );
       // console.log("Reject API Response:", response); // Log the response
        return response;
      } catch (error) {
        console.error("Reject API Error:", error); // Log the error
        throw error; // Re-throw the error to trigger onError
      }
    },
    onSuccess(data: any) {
      //console.log("Reject Success:", data); // Debugging
      setNotification({
        type: "success",
        message: data.data?.Message || "Picture Rejected Successfully",
      });
      setNOpen(true);
      queryClient.invalidateQueries(["all-picture-request"]);
    },
    onError(error: any) {
      console.error("Reject Error:", error); // Debugging
      setNotification({
        type: "error",
        message: error.response?.data?.Message || "Error Occurred",
      });
      setNOpen(true);
    },
    onSettled() {
      //console.log("Reject Settled"); // Debugging
      setOpen(false);
    },
  });

  return (
    <Flex flexDir="column" minWidth="600px" gap="50px">
      <Flex gap="10px" flexDir="column">
        <Flex gap="20px">
          <span>Student No:</span>
          <Text m="0" fontWeight="600">
            {props?.StudentNo}
          </Text>
        </Flex>
        <Flex gap="20px">
          <span>Student Name:</span>
          <Text m="0" fontWeight="600">
            {props?.StudentName || "N/A"}
          </Text>
        </Flex>
        <Flex gap="20px">
          <span>Created Date:</span>
          <Text m="0" fontWeight="600">
            {props?.CreateDate ? format(new Date(props.CreateDate), "dd/MM/yyyy") : "N/A"}
          </Text>
        </Flex>
        <Flex gap="20px">
          <span>Approved Date:</span>
          <Text m="0" fontWeight="600">
            {props?.ActionDate ? format(new Date(props.ActionDate), "dd/MM/yyyy") : "N/A"}
          </Text>
        </Flex>
        <Flex gap="40px">
          <span>Approval-Type:</span>
          <Text m="0" fontWeight="600">
            {props?.ApprovalType}
          </Text>
        </Flex>
        <Flex gap="43px">
          <span>Approved-By:</span>
          <Text m="0" fontWeight="600">
            {props?.ActionBy}
          </Text>
        </Flex>
        <Flex gap="43px">
          <span>Status:</span>
          <Text m="0" fontWeight="600">
            {props?.ApprovalStatus}
          </Text>
        </Flex>
      </Flex>
      <Flex justify="center" gap="30px" align="center">
        <Button
          onClick={() => handleApprove(props.ApprovalId)} // Use props.ApprovalId
          bg="var(--dark-green-color)"
          color="var(--white-color)"
          width="130px"
          disabled={isApproving || isRejecting} // Disable if either is loading
        >
          {isApproving ? "Approving..." : "Approve"}
        </Button>
        <Button
          onClick={() => handleReject(props.ApprovalId)} // Use props.ApprovalId
          colorScheme="red"
          width="130px"
          disabled={isApproving || isRejecting} // Disable if either is loading
        >
          {isRejecting ? "Rejecting..." : "Reject"}
        </Button>
      </Flex>
    </Flex>
  );
};